import type { ProductDirectoryResponse, ProductDirectoryShortcut } from './product-directory-api';

/*
 * Everything the directory can already name, drawn once so the starred band and the add control
 * read the same catalog: what the server resolved, every product in the catalog, and the open
 * product's own actions and applications.
 */
function getKnownShortcuts(directory: ProductDirectoryResponse | null): Map<string, ProductDirectoryShortcut> {
  const known = new Map<string, ProductDirectoryShortcut>();
  if (directory === null) {
    return known;
  }
  directory.shortcuts.forEach((shortcut) => {
    known.set(shortcut.token, shortcut);
  });
  directory.products.forEach((product) => {
    known.set(product.name, {
      token: product.name,
      label: product.label,
      badge: product.badge,
      category: product.category,
      url: product.url,
      kind: 'product',
    });
  });
  const openProduct = directory.products.find((product) => product.name === directory.current);
  let badge = '';
  let category = 'platform';
  if (openProduct !== undefined) {
    badge = openProduct.badge;
    category = openProduct.category;
  }
  directory.jobs.forEach((job) => {
    known.set(job.token, {
      token: job.token,
      label: job.label,
      badge: badge,
      category: category,
      url: job.path,
      kind: 'action',
    });
  });
  directory.applications.forEach((application) => {
    known.set(application.token, {
      token: application.token,
      label: application.label,
      badge: badge,
      category: category,
      url: application.path,
      kind: 'application',
      icon: application.icon,
    });
  });
  return known;
}

/*
 * The arrangement is the member's own list. Ordering the known catalog by the live favourites
 * rather than reading the server's list means a star lands in the band on the click, not on the
 * next fetch — and a shortcut belonging to a product that is not open still draws, because the
 * server resolved that one for us.
 */
function arrangeShortcuts(directory: ProductDirectoryResponse | null, favorites: string[]): ProductDirectoryShortcut[] {
  const known = getKnownShortcuts(directory);
  const ordered: ProductDirectoryShortcut[] = [];
  favorites.forEach((token) => {
    const shortcut = known.get(token);
    if (shortcut !== undefined) {
      ordered.push(shortcut);
    }
  });
  return ordered;
}

/** Everything the member could still star, in catalog order — the list the add control offers. */
function arrangeUnstarredShortcuts(
  directory: ProductDirectoryResponse | null,
  favorites: string[]
): ProductDirectoryShortcut[] {
  const starred = new Set(favorites);
  const known = Array.from(getKnownShortcuts(directory).values());
  return known.filter((shortcut) => !starred.has(shortcut.token));
}

/** The open product's applications in catalog order, drawn the way the shortcuts band draws them. */
function arrangeApplications(directory: ProductDirectoryResponse | null): ProductDirectoryShortcut[] {
  let applications: ProductDirectoryShortcut[] = [];
  if (directory !== null) {
    const known = getKnownShortcuts(directory);
    applications = directory.applications
      .map((application) => known.get(application.token))
      .filter((shortcut): shortcut is ProductDirectoryShortcut => shortcut !== undefined);
  }
  return applications;
}

export { arrangeApplications, arrangeShortcuts, arrangeUnstarredShortcuts };
